import { useTheme } from 'contexts'
import { LinearGradient } from 'expo-linear-gradient'
import React, { useCallback, useState } from 'react'
import { Pressable, StyleSheet } from 'react-native'

import { ImageContainer } from 'partials/EditableAvatar/styled'
import { UploadButton } from 'partials/UploadButton'

import { baseCrop, defaultAvatarImage } from 'widgets/Avatar/defaultAvatarImage'
import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { ImageCropper } from 'widgets/ImageCropper'
import { Modal } from 'widgets/Modal'
import { Text } from 'widgets/Text'

import { useCoverUpload } from 'hooks/useCoverUpload'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFileSelect } from 'hooks/useFileSelect'
import { useGroup } from 'hooks/useGroup'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { useProgram } from 'hooks/useProgram'

import commonStyles from 'utils/styles/commonStyles'

import { SelectedFile } from 'types/file'
import { Media, MediaCrop } from 'types/media'
import { ProgramType } from 'types/program'

type Props = {
  programId?: string
  showGradient?: boolean
}

const styles = StyleSheet.create({
  gradient: {
    width: '100%',
    height: '25%',
  },
})

export const EditableUpspaceCover = ({ programId, showGradient = false }: Props) => {
  const theme = useTheme()
  const { width: maxWidth } = useMaxWidth()

  const { data: currentUser } = useCurrentUser()
  const { data: group, updateGroup } = useGroup({ userId: currentUser?.id })
  const { data: program, updateProgram } = useProgram(programId)

  const updateCover = programId ? updateProgram : updateGroup
  const updateData = programId ? program : group

  const { handleFileSelect, handleReset, selectedFile, selectedFileUrl } = useFileSelect()

  const type = programId ? ProgramType.PROGRAM : ProgramType.GROUP
  const { mutateAsync: uploadUpspaceCover } = useCoverUpload(type)

  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [crop, setCrop] = useState<MediaCrop>(baseCrop)

  const onCropChange = (newCrop: MediaCrop) => setCrop(newCrop)

  const handleClose = useCallback(() => {
    if (isSaving) {
      return
    }

    setIsEditing(false)
    setCrop(baseCrop)
    handleReset()
  }, [handleReset, isSaving])

  const uploadUpspaceCoverToProgram = useCallback(
    async (file: SelectedFile | Media) => {
      if (!updateData?.id) return
      setIsSaving(true)

      const uploadedUpspaceCover = await uploadUpspaceCover({
        data: {
          crop,
          file,
          programId: updateData.id,
        },
      })

      updateCover.mutate({
        ...updateData,
        cover: uploadedUpspaceCover,
      })

      handleClose()
      setIsSaving(false)
    },
    [crop, handleClose, updateCover, uploadUpspaceCover, updateData],
  )

  const handleSave = useCallback(() => {
    uploadUpspaceCoverToProgram(selectedFile || defaultAvatarImage)
  }, [selectedFile, uploadUpspaceCoverToProgram])

  return (
    <>
      <Box width={maxWidth} height={maxWidth}>
        {updateData?.cover && (
          <>
            <Box
              testID='CoverImageContainer'
              position='absolute'
              display='flex'
              width='100%'
              height='100%'
              align='flex-end'
              justifyContent='flex-end'
              backgroundColor='greyLight'
            >
              <Image
                resizeMode='cover'
                source={{ uri: updateData?.cover.url }}
                style={commonStyles.fullSize}
              />

              <Box padding='md' position='absolute'>
                <Pressable>
                  <Icon name='camera' color='white' size={24} />
                  <Box position='absolute' height='100%' width='100%'>
                    <UploadButton
                      fileType='image'
                      onFileSelect={(file: any) => {
                        setIsEditing(true)
                        handleFileSelect(file)
                      }}
                    />
                  </Box>
                </Pressable>
              </Box>
            </Box>
          </>
        )}

        {!updateData?.cover && (
          <Box
            testID='CoverImageContainer'
            position='absolute'
            display='flex'
            justifyContent='center'
            align='center'
            width='100%'
            height={showGradient ? '60%' : '100%'}
          >
            <Pressable>
              <Box align='center' justifyContent='center'>
                <Box margin={{ bottom: 'sm' }}>
                  <Icon name='camera' color='black' size={32} />
                </Box>
                <Text fontSize='xs' align='center' numberOfLines={1} color='black'>
                  Upload Photo
                </Text>
              </Box>
              <Box position='absolute' height='100%' width='100%'>
                <UploadButton
                  fileType='image'
                  onFileSelect={(file: any) => {
                    setIsEditing(true)
                    handleFileSelect(file)
                  }}
                />
              </Box>
            </Pressable>
          </Box>
        )}

        {(showGradient || updateData?.cover) && (
          <Box
            testID='GradientContainer'
            pointerEvents='none'
            position='absolute'
            display='flex'
            width='100%'
            height='100%'
            flexDirection='column'
            justifyContent='flex-start'
          >
            <LinearGradient
              style={styles.gradient}
              colors={[theme.colors.black50, 'transparent']}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
            />
          </Box>
        )}
      </Box>

      <Modal
        actionButtonProps={[
          {
            disabled: isSaving,
            onPress: handleClose,
            title: 'Cancel',
            type: 'v2primary',
          },
          {
            disabled: !selectedFile || isSaving,
            onPress: handleSave,
            title: 'Save',
            type: 'v2primary',
          },
        ]}
        isVisible={isEditing}
        onClose={handleClose}
        title='Edit Cover Image'
      >
        <ImageContainer
          flex={1}
          margin={{ vertical: 'xl' }}
          align='center'
          justifyContent='space-between'
        >
          {selectedFileUrl ? (
            <ImageCropper src={selectedFileUrl} cropSettings={crop} onChange={onCropChange} />
          ) : null}
        </ImageContainer>
      </Modal>
    </>
  )
}
